import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import {
  Search, Plus, Edit, UserCheck, UserX,
  X, Phone, Building2, Shield, ChevronLeft, ChevronRight
} from 'lucide-react'
import { medicalApi, hospitalsApi, usersApi } from '../../lib/api'

interface StaffItem {
  id: string
  account_id: string
  account_name?: string
  account_phone?: string
  name: string
  phone: string
  hospital_id: string
  hospital_name?: string
  medical_role: string
  status?: string
  created_at: string
}

interface Hospital { id: string; name: string }
interface UserItem { id: string; account_id?: string; member_no?: string; name: string; phone: string; status: string; role: string }

const MedicalStaff = () => {
  const [staffs, setStaffs] = useState<StaffItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const [searchTerm, setSearchTerm] = useState('')
  const [roleFilter, setRoleFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 12

  const [showModal, setShowModal] = useState(false)
  const [modalType, setModalType] = useState<'add' | 'edit'>('add')
  const [selectedStaff, setSelectedStaff] = useState<StaffItem | null>(null)
  const [formLoading, setFormLoading] = useState(false)

  const [hospitals, setHospitals] = useState<Hospital[]>([])
  const [formData, setFormData] = useState({
    hospital_id: '',
    medical_role: 'staff'
  })

  // 判断当前用户是否为机构负责人
  const isSupervisor = (() => {
    try {
      const authUser = JSON.parse(localStorage.getItem('auth_user') || '{}')
      return authUser.role === 'medical' && authUser.medicalRole === 'supervisor'
    } catch { return false }
  })()

  const [confirmingStaff, setConfirmingStaff] = useState<StaffItem | null>(null)
  const [confirmType, setConfirmType] = useState<'freeze' | 'unfreeze' | 'logout' | 'activate'>('freeze')

  // 绑定用户相关
  const [boundUserId, setBoundUserId] = useState('')
  const [boundUserInfo, setBoundUserInfo] = useState<{name: string; phone: string; member_no?: string} | null>(null)
  const [userSearch, setUserSearch] = useState('')
  const [userResults, setUserResults] = useState<UserItem[]>([])
  const [showUserDropdown, setShowUserDropdown] = useState(false)

  const fetchStaffs = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const params: Record<string, unknown> = {
        page: currentPage,
        page_size: pageSize,
      }
      if (searchTerm) params.keyword = searchTerm
      if (roleFilter !== 'all') params.medical_role = roleFilter

      const res = await medicalApi.list(params) as { total: number; staff?: StaffItem[]; items?: StaffItem[] }
      let items = res.items || res.staff || []
      // 前端过滤状态
      if (statusFilter !== 'all') {
        items = items.filter(s => {
          if (statusFilter === 'active') return s.status === 'active'
          if (statusFilter === 'frozen') return s.status === 'frozen'
          if (statusFilter === 'inactive') return s.status === 'inactive'
          return true
        })
      }
      setStaffs(items)
      setTotal(res.total || 0)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setLoading(false)
    }
  }, [currentPage, searchTerm, roleFilter, statusFilter])

  useEffect(() => { fetchStaffs() }, [fetchStaffs])

  // 防抖搜索
  useEffect(() => {
    const t = setTimeout(() => setCurrentPage(1), 400)
    return () => clearTimeout(t)
  }, [searchTerm, roleFilter, statusFilter])

  // 加载机构列表
  useEffect(() => {
    hospitalsApi.list({ page_size: 100 } as any).then((res: any) => {
      setHospitals(res.hospitals || res.items || [])
    }).catch(() => {})
  }, [])

  // 用户搜索（搜索已注册的用户账号）
  useEffect(() => {
    if (!userSearch || userSearch.length < 2) { setUserResults([]); return }
    const t = setTimeout(async () => {
      try {
        const res = await usersApi.list({ keyword: userSearch, page_size: 20 }) as { users: UserItem[] }
        setUserResults((res.users || []).filter((u: UserItem) => u.status === 'active'))
      } catch { setUserResults([]) }
    }, 400)
    return () => clearTimeout(t)
  }, [userSearch])

  const totalPages = Math.ceil(total / pageSize)

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'staff': return '医护人员'
      case 'supervisor': return '机构负责人'
      default: return role
    }
  }

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'staff': return 'bg-blue-100 text-blue-700'
      case 'supervisor': return 'bg-purple-100 text-purple-700'
      case 'doctor': return 'bg-green-100 text-green-700'
      case 'nurse': return 'bg-pink-100 text-pink-700'
      default: return 'bg-gray-100 text-gray-700'
    }
  }

  const maskPhone = (p: string) => p ? p.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2') : '-'

  const openAddModal = () => {
    setModalType('add')
    // 机构负责人自动填充所属机构，不可修改
    const supHospitalId = isSupervisor ? (() => {
      try {
        const authUser = JSON.parse(localStorage.getItem('auth_user') || '{}')
        return authUser.hospitalId || ''
      } catch { return '' }
    })() : ''
    setFormData({ hospital_id: supHospitalId, medical_role: 'staff' })
    setBoundUserId('')
    setBoundUserInfo(null)
    setUserSearch('')
    setUserResults([])
    setShowUserDropdown(false)
    setShowModal(true)
  }

  const openEditModal = (staff: StaffItem) => {
    setSelectedStaff(staff)
    setModalType('edit')
    setFormData({
      hospital_id: staff.hospital_id,
      medical_role: staff.medical_role
    })
    setBoundUserId(staff.account_id || '')
    setBoundUserInfo(staff.account_id ? { name: staff.account_name || staff.name, phone: staff.account_phone || staff.phone } : null)
    setUserSearch('')
    setUserResults([])
    setShowUserDropdown(false)
    setShowModal(true)
  }

  const handleSubmit = async () => {
    if (modalType === 'add' && !boundUserId) { alert('请搜索选择要绑定的用户账号'); return }
    if (!formData.hospital_id) { alert('请选择所属机构'); return }

    setFormLoading(true)
    try {
      if (modalType === 'add') {
        await medicalApi.create({
          account_id: boundUserId,
          hospital_id: formData.hospital_id,
          medical_role: formData.medical_role,
        })
      } else if (selectedStaff) {
        await medicalApi.update(selectedStaff.id, {
          hospital_id: formData.hospital_id,
          medical_role: formData.medical_role,
        })
      }
      setShowModal(false)
      fetchStaffs()
    } catch (e) {
      alert((e as Error).message)
    } finally {
      setFormLoading(false)
    }
  }

  const handleConfirmAction = async () => {
    if (!confirmingStaff) return
    try {
      if (confirmType === 'logout') {
        await medicalApi.delete(confirmingStaff.id)
      } else if (confirmType === 'activate') {
        // 重新激活
        await medicalApi.updateStatus(confirmingStaff.id, 'unfreeze')
      } else if (confirmType === 'freeze') {
        await medicalApi.updateStatus(confirmingStaff.id, 'freeze')
      } else if (confirmType === 'unfreeze') {
        await medicalApi.updateStatus(confirmingStaff.id, 'unfreeze')
      }
      setConfirmingStaff(null)
      fetchStaffs()
    } catch (e) {
      alert((e as Error).message)
    }
  }

  // 获取已绑定用户显示信息
  const getBoundUserDisplay = () => {
    // 优先使用已保存的用户信息
    if (boundUserInfo) {
      return boundUserInfo
    }
    // 编辑模式下从已选员工信息中获取
    if (selectedStaff?.account_id === boundUserId) {
      return { name: selectedStaff.account_name || selectedStaff.name, phone: selectedStaff.account_phone || selectedStaff.phone }
    }
    // 未知用户
    return { name: '未知用户', phone: '-' }
  }

  return (
    <div className="p-6 space-y-6">
      {/* 页面标题 */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">医护人员管理</h1>
          <p className="text-gray-500 text-sm mt-1">共 {total} 位医护人员</p>
        </div>
        <button
          onClick={openAddModal}
          className="flex items-center gap-2 bg-green-700 text-white px-4 py-2 rounded-xl hover:bg-green-800 transition-colors"
        >
          <Plus size={18} />
          <span>添加人员</span>
        </button>
      </div>

      {/* 筛选和搜索 */}
      <div className="bg-white rounded-2xl shadow-md p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="搜索姓名、手机号、机构..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div className="flex gap-3">
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="all">全部角色</option>
              <option value="staff">医护人员</option>
              <option value="supervisor">机构负责人</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="all">全部状态</option>
              <option value="active">正常</option>
              <option value="frozen">冻结</option>
              <option value="inactive">注销</option>
            </select>
          </div>
        </div>
      </div>

      {/* 医护人员列表 */}
      {loading ? (
        <div className="text-center py-20 text-gray-400">加载中...</div>
      ) : error ? (
        <div className="text-center py-20 text-red-500">{error}</div>
      ) : staffs.length === 0 ? (
        <div className="text-center py-20 text-gray-400">暂无数据</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {staffs.map((staff) => (
            <div key={staff.id} className="bg-white rounded-2xl shadow-md p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center">
                    <span className="text-white text-xl font-bold">
                      {(staff.account_name || staff.name || '?').charAt(0)}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800">{staff.account_name || staff.name || '-'}</h3>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getRoleBadgeColor(staff.medical_role)}`}>
                      {getRoleLabel(staff.medical_role)}
                    </span>
                  </div>
                </div>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  staff.status === 'inactive'
                    ? 'bg-gray-100 text-gray-500'
                    : staff.status === 'frozen'
                    ? 'bg-orange-100 text-orange-600'
                    : 'bg-green-100 text-green-700'
                }`}>
                  {staff.status === 'inactive' ? '注销' : staff.status === 'frozen' ? '冻结' : '正常'}
                </span>
              </div>

              <div className="space-y-2 mb-4 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <Phone size={14} />
                  <span>{maskPhone(staff.account_phone || staff.phone)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Building2 size={14} />
                  <span>{staff.hospital_name || '-'}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-400">
                  <Shield size={14} />
                  <span>ID: {staff.id.slice(0, 8)}...</span>
                </div>
              </div>

              <div className="flex gap-2 pt-3 border-t">
                <button
                  onClick={() => openEditModal(staff)}
                  className="flex-1 flex items-center justify-center gap-1 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  <Edit size={14} />
                  <span className="text-sm">编辑</span>
                </button>
                <button
                  onClick={() => {
                    setConfirmType(staff.status === 'frozen' ? 'unfreeze' : 'freeze')
                    setConfirmingStaff(staff)
                  }}
                  className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-lg transition-colors ${
                    staff.status === 'frozen'
                      ? 'text-green-600 hover:bg-green-50'
                      : 'text-orange-500 hover:bg-orange-50'
                  }`}
                >
                  {staff.status === 'frozen' ? (
                    <>
                      <UserCheck size={14} />
                      <span className="text-sm">解冻</span>
                    </>
                  ) : (
                    <>
                      <UserX size={14} />
                      <span className="text-sm">冻结</span>
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setConfirmType(staff.status === 'inactive' ? 'activate' : 'logout')
                    setConfirmingStaff(staff)
                  }}
                  className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-lg transition-colors ${
                    staff.status === 'inactive'
                      ? 'text-green-600 hover:bg-green-50'
                      : 'text-red-600 hover:bg-red-50'
                  }`}
                >
                  {staff.status === 'inactive' ? (
                    <>
                      <UserCheck size={14} />
                      <span className="text-sm">激活</span>
                    </>
                  ) : (
                    <>
                      <UserX size={14} />
                      <span className="text-sm">注销</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 分页 */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
            className="p-2 border rounded-lg disabled:opacity-40 hover:bg-gray-50">
            <ChevronLeft size={16} />
          </button>
          <span className="px-3 py-1 text-sm">{currentPage} / {totalPages}</span>
          <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
            className="p-2 border rounded-lg disabled:opacity-40 hover:bg-gray-50">
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* 添加/编辑模态框 */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-lg mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-bold text-gray-800">
                {modalType === 'add' ? '添加医护人员' : '编辑医护人员'}
              </h3>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* 绑定用户账号 - 置顶 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  绑定用户账号 <span className="text-red-500">*</span>
                </label>
                {boundUserId ? (
                  <div className="flex items-center justify-between p-3 bg-green-50 border-2 border-green-200 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-sm">
                          {getBoundUserDisplay().name.charAt(0)}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-gray-800">
                            {getBoundUserDisplay().name}
                            {getBoundUserDisplay().member_no && <span className="ml-1 text-xs text-gray-400">({getBoundUserDisplay().member_no})</span>}
                          </p>
                          <p className="text-xs text-gray-500">
                            {getBoundUserDisplay().phone}
                          </p>
                        </div>
                      </div>
                    </div>
                    {modalType === 'add' && (
                      <button
                        onClick={() => { setBoundUserId(''); setBoundUserInfo(null) }}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="text"
                      placeholder="搜索用户姓名或手机号..."
                      value={userSearch}
                      onChange={(e) => {
                        setUserSearch(e.target.value)
                        setShowUserDropdown(e.target.value.length > 0)
                      }}
                      className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                    {showUserDropdown && userResults.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                        {userResults.map((u) => (
                          <button
                            key={u.id}
                            onClick={() => {
                              setBoundUserId(u.account_id || u.id)
                              setBoundUserInfo({ name: u.name, phone: u.phone, member_no: u.member_no })
                              setUserSearch('')
                              setShowUserDropdown(false)
                            }}
                            className="w-full flex items-center gap-3 p-3 hover:bg-green-50 transition-colors text-left"
                          >
                            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-green-700 font-bold text-xs">{u.name?.charAt(0) || '?'}</span>
                            </div>
                            <div>
                              <p className="font-medium text-gray-800 text-sm">{u.name} {u.member_no && <span className="text-xs text-gray-400">({u.member_no})</span>}</p>
                              <p className="text-xs text-gray-400">{u.phone}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {showUserDropdown && userResults.length === 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg p-4 text-center text-gray-400 text-sm">
                        未找到匹配的用户
                      </div>
                    )}
                  </div>
                )}
                <p className="text-xs text-gray-400 mt-1">
                  请搜索选择用户信息，如果暂未注册，请<Link to="/admin/users" className="text-green-600 underline hover:text-green-700">点此添加账号</Link>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  所属机构 {isSupervisor && <span className="text-green-600 text-xs">（自动绑定当前机构）</span>}
                </label>
                {isSupervisor ? (
                  <div className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-gray-50 text-gray-500">
                    {hospitals.find(h => h.id === formData.hospital_id)?.name || '加载中...'}
                  </div>
                ) : (
                <select
                  value={formData.hospital_id}
                  onChange={(e) => setFormData({ ...formData, hospital_id: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="">请选择机构</option>
                  {hospitals.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
                </select>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  角色
                </label>
                <select
                  value={formData.medical_role}
                  onChange={(e) => setFormData({ ...formData, medical_role: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="staff">医护人员</option>
                  <option value="supervisor">机构负责人</option>
                </select>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t">
              <button
                onClick={() => setShowModal(false)}
                className="px-6 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleSubmit}
                disabled={formLoading}
                className="px-6 py-2 bg-green-700 text-white rounded-xl hover:bg-green-800 transition-colors disabled:opacity-50"
              >
                {formLoading ? '提交中...' : modalType === 'add' ? '添加' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 冻结/注销确认弹窗 */}
      {confirmingStaff && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-sm mx-4 shadow-2xl overflow-hidden">
            <div className="p-6 text-center">
              <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 ${
                confirmType === 'logout' || confirmType === 'freeze' ? 'bg-red-100' : 'bg-green-100'
              }`}>
                {confirmType === 'logout' || confirmType === 'freeze' ? (
                  <UserX className="w-8 h-8 text-red-600" />
                ) : (
                  <UserCheck className="w-8 h-8 text-green-600" />
                )}
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">
                {confirmType === 'freeze' && '确认冻结'}
                {confirmType === 'unfreeze' && '确认解冻'}
                {confirmType === 'logout' && '确认注销'}
                {confirmType === 'activate' && '确认激活'}
              </h3>
              <p className="text-gray-500 text-sm mb-6">
                确定要{confirmType === 'freeze' ? '冻结' : confirmType === 'unfreeze' ? '解冻' : confirmType === 'logout' ? '注销' : '激活'}账号 <strong>{confirmingStaff.name}</strong> 吗？
                {confirmType === 'freeze' && '冻结后该账号将无法登录。'}
                {confirmType === 'logout' && '注销后该账号将无法恢复使用。'}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmingStaff(null)}
                  className="flex-1 py-2.5 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 transition-colors font-medium"
                >
                  取消
                </button>
                <button
                  onClick={handleConfirmAction}
                  className={`flex-1 py-2.5 rounded-xl text-white font-medium transition-colors ${
                    confirmType === 'logout' || confirmType === 'freeze'
                      ? 'bg-red-600 hover:bg-red-700'
                      : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  确认
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MedicalStaff
